import { t } from '../../../../../common/i18n.js'
import type { Language } from '../../../../../common/types/i18n.js'
import type { TimelineEntry } from '../../../../components/portal-timeline/portal-timeline.js'
import { getWorkModeLabel } from '../../utils/format.js'
import type { EmploymentHistoryRecord } from './employee-profile-types.js'

/**
 * Maps SCD2 employment-history records to read-only `PortalTimeline` entries.
 *
 * Shared by the admin Job & Pay tab and the employee-visible Employment History
 * tab. The result carries no row `action` — callers that need one (e.g. the
 * admin tab's "delete future record") layer it on afterwards.
 */
export const toEmploymentTimelineEntries = (
    records: EmploymentHistoryRecord[],
    employeeStatus: string | null,
    lang: Language,
): TimelineEntry[] => {
    const ph = t.portal
    return records.map((rec): TimelineEntry => {
        const isCurrent = !rec.effective_to && employeeStatus === 'active'
        return {
            id: rec.id,
            label: rec.job_title ?? ph.compensation.position(lang),
            sublabel: rec.teamNames ?? undefined,
            effectiveFrom: rec.effective_from,
            effectiveTo: rec.effective_to,
            isCurrent,
            isFuture: new Date(rec.effective_from) > new Date(),
            badgeVariant: isCurrent ? 'current' : 'past',
            badge: isCurrent ? ph.compensation.current(lang) : undefined,
            details: [
                ...(rec.locationName
                    ? [{ label: ph.profile.location(lang), value: rec.locationName }]
                    : []),
                ...(rec.managerName
                    ? [{ label: ph.profile.manager(lang), value: rec.managerName }]
                    : []),
                ...(rec.work_mode
                    ? [
                          {
                              label: ph.profile.workMode(lang),
                              value: getWorkModeLabel(rec.work_mode, lang),
                          },
                      ]
                    : []),
            ],
        }
    })
}
